import { type JSX, ReactNode, useState } from 'react'

import { Icon, Popover, PopoverContent, PopoverTrigger, cn } from '@hap/components'

interface InlinePopoverCellProps {
  /** The content displayed in the trigger (the selected value) */
  children?: ReactNode
  /** Shown when children is falsy */
  placeholder?: ReactNode
  /** Minimum width for the trigger content area */
  minWidth?: number
  /** The popover content (typically a CommandList) */
  content: ReactNode
  /** Controlled open state */
  open?: boolean
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void
  /** Alignment of the popover content */
  align?: 'start' | 'center' | 'end'
  /** Additional classes for the PopoverContent */
  contentClassName?: string
  /** Additional classes for the wrapper div */
  className?: string
}

/**
 * A generic inline popover component for table cells.
 *
 * Features:
 * - Shows a subtle border on row hover
 * - Triangle indicator that rotates when open
 * - Consistent styling across all table cell popovers
 *
 * @example
 * // Basic usage with text
 * <InlinePopoverCell
 *   open={open}
 *   onOpenChange={setOpen}
 *   content={<RolesCommandList onSelect={handleSelect} />}
 * >
 *   {role?.name}
 * </InlinePopoverCell>
 *
 * @example
 * // With icon and placeholder
 * <InlinePopoverCell
 *   open={open}
 *   onOpenChange={setOpen}
 *   minWidth={120}
 *   placeholder="Select workspace"
 *   content={<WorkspacesCommandList onSelect={handleSelect} />}
 * >
 *   <Icon name="globe" className="size-4 opacity-50" />
 *   Organization-wide
 * </InlinePopoverCell>
 */
export function InlinePopoverCell({
  children,
  placeholder,
  minWidth,
  content,
  open: controlledOpen,
  onOpenChange,
  align = 'end',
  contentClassName,
  className,
}: InlinePopoverCellProps): JSX.Element {
  const [internalOpen, setInternalOpen] = useState(false)

  // Support both controlled and uncontrolled modes
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen
  const setOpen = (value: boolean) => {
    if (!isControlled) {
      setInternalOpen(value)
    }
    onOpenChange?.(value)
  }

  const hasValue = children !== null && children !== undefined && children !== ''
  const displayContent = hasValue ? children : placeholder

  return (
    <div className={cn('-ml-2 mr-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          className={cn(
            'hover:bg-input-background data-[state=open]:bg-input-secondary-background group flex items-center gap-2 rounded p-2 text-left',
            'group-hover/row:border-foreground/20',
          )}
        >
          <span
            className={cn('flex shrink grow items-center gap-2 truncate', {
              'opacity-0 group-hover/row:opacity-100 group-data-[state=open]:opacity-100':
                !hasValue && placeholder,
            })}
            style={minWidth ? { minWidth: `${minWidth}px` } : undefined}
          >
            {displayContent}
          </span>
          <Icon
            name="triangle"
            weight="fill"
            className={cn(
              'text-foreground/30 relative -mt-px w-3 rotate-180',
              'opacity-0 group-hover/row:opacity-100',
              'group-data-[state=open]:rotate-0 group-data-[state=open]:opacity-100',
            )}
          />
        </PopoverTrigger>
        <PopoverContent align={align} className={cn('w-auto p-0', contentClassName)}>
          {content}
        </PopoverContent>
      </Popover>
    </div>
  )
}
